import { PagePresentation } from '@Generated/graphql'

import { TileSettings } from './settings'

/*
 * Image loader `sizes` hints for v2 tiles, computed from the same layout model the CSS
 * implements — this file mirrors TilesV2.module.scss (track math, gap, size presets) and
 * BlockContainer/styles.module.scss (page padding, inner max-width, common rail), so the
 * hint tracks the rendered tile width per breakpoint instead of a single px guess.
 */

const GAP = 6
// BlockContainer: 12px side padding, .inner max-width
const PAGE_PADDING = 24
const PAGE_MAX_WIDTH = 2500
// common on the left/right reserves its rail from tablet up: 340px content
// + 2×18px content padding + 24px flex gap
const COMMON_RAIL = 424
// faceted lists show the filter sidebar from desktop up: 320px column + 15px margin
// (see Blocks/ProductList/v2.module.scss .actions)
const FACET_RAIL = 335
// Sidebar presentation renders in a right-aligned NativeModal drawer: 90vw, capped at
// 600px from tablet (the 420px drawer is the left-aligned filter modal, not this one)
const DRAWER_VW = 90
const DRAWER_MAX_WIDTH = 600

const PHABLET = 480
const STABLET = 640
const TABLET = 768
const DESKTOP = 1024
const HUGE = 1600

// --tile-size-* in TilesV2.module.scss
const SIZE_PX: Record<string, number> = {
  large: 370,
  medium: 300,
  small: 168,
  xsmall: 120,
}

// Piecewise-linear width: from `from`px viewport up, width = (vw / 100) · viewport + px
interface Segment {
  from: number
  px: number
  vw: number
}

// A px value that steps at breakpoints (slider card width, fluid-grid min track)
interface Step {
  from: number
  value: number
}

export interface TileContainerContext {
  // BlockContainer common position, only when common content actually renders
  commonPosition?: string | null
  // 'half' when the common takes half the row (single-look presentation)
  commonWidth?: string | null
  // faceted list: the desktop filter sidebar takes its rail off the grid
  facetRail?: boolean
  noPadding?: boolean
  presentation?: PagePresentation | null
}

function containerSegments({ commonPosition, commonWidth, facetRail, noPadding, presentation }: TileContainerContext): Segment[] {
  if (presentation === PagePresentation.Sidebar) {
    return [
      { from: 0, px: 0, vw: DRAWER_VW },
      { from: TABLET, px: DRAWER_MAX_WIDTH, vw: 0 },
    ]
  }

  // .inner's max-width sits INSIDE the container padding: the inner keeps
  // growing until the viewport reaches max-width + padding, and rests at the
  // full max-width — so cap branches start at +pad and don't subtract it
  const pad = noPadding ? 0 : PAGE_PADDING
  const hasSideCommon = commonPosition === 'left' || commonPosition === 'right'

  if (facetRail) {
    return [
      { from: 0, px: -pad, vw: 100 },
      { from: DESKTOP, px: -(pad + FACET_RAIL), vw: 100 },
      { from: PAGE_MAX_WIDTH + pad, px: PAGE_MAX_WIDTH - FACET_RAIL, vw: 0 },
    ]
  }

  if (hasSideCommon && commonWidth === 'half') {
    // the common stacks on top below tablet; from tablet it takes ~50% of the row
    return [
      { from: 0, px: -pad, vw: 100 },
      { from: TABLET, px: -(pad / 2 + PAGE_PADDING), vw: 50 },
      { from: PAGE_MAX_WIDTH + pad, px: PAGE_MAX_WIDTH / 2 - PAGE_PADDING, vw: 0 },
    ]
  }

  if (hasSideCommon) {
    return [
      { from: 0, px: -pad, vw: 100 },
      { from: TABLET, px: -(pad + COMMON_RAIL), vw: 100 },
      { from: PAGE_MAX_WIDTH + pad, px: PAGE_MAX_WIDTH - COMMON_RAIL, vw: 0 },
    ]
  }

  // .top.noPadding also drops the inner max-width, so the block spans the full viewport
  if (noPadding) {
    return [{ from: 0, px: 0, vw: 100 }]
  }

  return [
    { from: 0, px: -pad, vw: 100 },
    { from: PAGE_MAX_WIDTH + pad, px: PAGE_MAX_WIDTH, vw: 0 },
  ]
}

// slider cards are --tile-size wide; size `auto` ramps 300 → 370 at phablet
function sliderCardSteps(size: string): Step[] {
  if (size === 'auto') {
    return [
      { from: 0, value: 300 },
      { from: PHABLET, value: SIZE_PX.large },
    ]
  }

  return [{ from: 0, value: SIZE_PX[size] || SIZE_PX.large }]
}

// fluid-grid min track: --tile-grid-min for size `auto` (168 → 300 at stablet), --tile-size otherwise
function gridMinSteps(size: string): Step[] {
  if (size === 'auto') {
    return [
      { from: 0, value: SIZE_PX.small },
      { from: STABLET, value: SIZE_PX.medium },
    ]
  }

  return [{ from: 0, value: SIZE_PX[size] || SIZE_PX.large }]
}

function segmentAt<T extends { from: number }>(segments: T[], viewport: number): T {
  return segments.reduce((match, segment) => (segment.from <= viewport ? segment : match), segments[0])
}

// candidate column counts for the fluid grid threshold solver (2..64)
const COLUMN_CANDIDATES = Array.from({ length: 63 }, (_, index) => index + 2)

const round2 = (value: number) => Math.ceil(value * 100) / 100

// Render one tile-width expression. A small negative px offset (container padding, a gap
// share) is dropped: the hint overshoots slightly, which errs on the sharp side.
function renderTileWidth(vw: number, px: number): string {
  if (vw === 0) {
    return `${Math.round(px)}px`
  }

  if (px > -60) {
    return `${round2(vw)}vw`
  }

  return `calc(${round2(vw)}vw - ${Math.round(-px)}px)`
}

interface Entry {
  from: number
  width: string
}

// entries ascending by viewport → sizes attribute (descending min-width, unconditional base)
function toSizes(entries: Entry[]): string {
  const merged = entries.filter((entry, index) => index === 0 || entry.width !== entries[index - 1].width)

  return merged
    .slice()
    .reverse()
    .map((entry, index, all) => (index === all.length - 1 ? entry.width : `(min-width: ${entry.from}px) ${entry.width}`))
    .join(', ')
}

// per-column-count tile width inside a container segment: (C - (n-1)·gap) / n
function tileWidth(container: Segment, columns: number): { px: number, vw: number } {
  return {
    px: (container.px - (columns - 1) * GAP) / columns,
    vw: container.vw / columns,
  }
}

/*
 * Fluid grid (columns "auto"): auto-fill/auto-fit tracks of at least the min size.
 * The column count is a step function of the viewport — solve the thresholds where an
 * extra track fits, then evaluate the track width in each region. `maxColumns` caps the
 * count for stretch (auto-fit collapses empty tracks, so tiles never split further than
 * the tile count); auto-fill keeps empty tracks, so it is uncapped.
 */
function fluidGridEntries(container: Segment[], minSteps: Step[], maxColumns: number): Entry[] {
  const events = new Set<number>([...container.map(s => s.from), ...minSteps.map(s => s.from)])
  const containerEnd = (segment: Segment) => container.find(s => s.from > segment.from)?.from ?? Infinity
  const stepEnd = (step: Step) => minSteps.find(s => s.from > step.from)?.from ?? Infinity

  container.filter(segment => segment.vw !== 0).forEach((segment) => {
    minSteps.forEach((step) => {
      const lo = Math.max(segment.from, step.from)
      const hi = Math.min(containerEnd(segment), stepEnd(step))
      if (lo >= hi) {
        return
      }

      COLUMN_CANDIDATES.filter(n => n <= maxColumns).forEach((n) => {
        const needed = n * step.value + (n - 1) * GAP
        const viewport = Math.ceil(((needed - segment.px) * 100) / segment.vw)
        if (viewport > lo && viewport < hi) {
          events.add(viewport)
        }
      })
    })
  })

  return [...events].sort((a, b) => a - b).map((from) => {
    const segment = segmentAt(container, from)
    const min = segmentAt(minSteps, from).value
    const width = (segment.vw / 100) * from + segment.px
    const columns = Math.min(maxColumns, Math.max(1, Math.floor((width + GAP) / (min + GAP))))
    const tile = tileWidth(segment, columns)

    return { from, width: renderTileWidth(tile.vw, tile.px) }
  })
}

// Explicit-columns grid: the responsive ladder caps the count at 2 / 3 (tablet) / 4 (desktop),
// full N from huge — mirrors .list.fixedCols
function fixedGridEntries(container: Segment[], columns: number): Entry[] {
  const ladder: Step[] = [
    { from: 0, value: 2 },
    { from: TABLET, value: 3 },
    { from: DESKTOP, value: 4 },
    { from: HUGE, value: columns },
  ]
  const events = new Set<number>([...container.map(s => s.from), ...ladder.map(s => s.from)])

  return [...events].sort((a, b) => a - b).map((from) => {
    const segment = segmentAt(container, from)
    const effective = Math.min(columns, segmentAt(ladder, from).value)
    const tile = tileWidth(segment, effective)

    return { from, width: renderTileWidth(tile.vw, tile.px) }
  })
}

// Explicit-columns slider: cards take a 1/N share of the container, floored at the size
// preset — mirrors .slider.fixedCols
function fixedSliderEntries(container: Segment[], columns: number, cards: Step[]): Entry[] {
  const events = new Set<number>([...container.map(s => s.from), ...cards.map(s => s.from)])

  container.filter(segment => segment.vw !== 0).forEach((segment) => {
    cards.forEach((card) => {
      // viewport where the 1/N share overtakes the card floor
      const needed = columns * card.value + (columns - 1) * GAP
      const viewport = Math.ceil(((needed - segment.px) * 100) / segment.vw)
      events.add(Math.max(viewport, 0))
    })
  })

  return [...events].sort((a, b) => a - b).map((from) => {
    const segment = segmentAt(container, from)
    const floor = segmentAt(cards, from).value
    const share = ((segment.vw / 100) * from + segment.px - (columns - 1) * GAP) / columns
    if (share < floor) {
      return { from, width: `${floor}px` }
    }

    const tile = tileWidth(segment, columns)

    return { from, width: renderTileWidth(tile.vw, tile.px) }
  })
}

export function calculateSizes(
  settings: Pick<TileSettings, 'columns' | 'enableSlider' | 'size' | 'stretch'>,
  count: number,
  context: TileContainerContext = {},
): string {
  const container = containerSegments(context)
  const fixedColumns = settings.columns !== 'auto' ? Math.max(1, Number(settings.columns) || 1) : undefined

  if (settings.enableSlider) {
    const cards = sliderCardSteps(settings.size)
    if (fixedColumns) {
      return toSizes(fixedSliderEntries(container, fixedColumns, cards))
    }

    return toSizes(cards.map(card => ({ from: card.from, width: `${card.value}px` })))
  }

  if (fixedColumns) {
    return toSizes(fixedGridEntries(container, fixedColumns))
  }

  const maxColumns = settings.stretch ? Math.max(1, count) : Infinity

  return toSizes(fluidGridEntries(container, gridMinSteps(settings.size), maxColumns))
}
